import { eventBytes, type SerializedEvent } from "./contract";

// Non-persistent, bounded FIFO for pending diagnostic events (ticket #127).
// Two independent caps; a new event that would exceed either is DROPPED (the
// older queued events are kept — they can hold the initiating failure before
// follow-on noise). Nothing is written to any persistent store.

export const MAX_QUEUE_EVENTS = 120;
export const MAX_QUEUE_BYTES = 512 * 1024;
// Per-batch delivery bounds (mirrors the BFF batch + body caps).
export const MAX_BATCH_EVENTS = 20;
export const MAX_BATCH_BYTES = 256 * 1024;

interface Entry {
  event: SerializedEvent;
  bytes: number;
}

export interface DiagnosticQueue {
  enqueue(event: SerializedEvent): boolean;
  /** Oldest FIFO slice within the batch caps, without removing it. */
  peekBatch(): SerializedEvent[];
  /** Remove exactly `count` oldest events (an acknowledged batch). */
  drop(count: number): void;
  size(): number;
  bytes(): number;
  clear(): void;
  droppedCount(): number;
}

export function createQueue(
  opts: { maxEvents?: number; maxBytes?: number } = {},
): DiagnosticQueue {
  const maxEvents = opts.maxEvents ?? MAX_QUEUE_EVENTS;
  const maxBytes = opts.maxBytes ?? MAX_QUEUE_BYTES;
  let entries: Entry[] = [];
  let totalBytes = 0;
  let dropped = 0;

  return {
    enqueue(event) {
      const bytes = eventBytes(event);
      if (entries.length + 1 > maxEvents || totalBytes + bytes > maxBytes) {
        dropped++;
        return false;
      }
      entries.push({ event, bytes });
      totalBytes += bytes;
      return true;
    },
    peekBatch() {
      const batch: SerializedEvent[] = [];
      let batchBytes = 0;
      for (const entry of entries) {
        if (batch.length >= MAX_BATCH_EVENTS) break;
        // Always allow the first event even if it alone exceeds the batch byte
        // cap, so an oversized-but-queued event can still be attempted (the BFF
        // will reject it and the batcher drops it permanently).
        if (batch.length > 0 && batchBytes + entry.bytes > MAX_BATCH_BYTES) break;
        batch.push(entry.event);
        batchBytes += entry.bytes;
      }
      return batch;
    },
    drop(count) {
      const removed = entries.slice(0, count);
      for (const e of removed) totalBytes -= e.bytes;
      entries = entries.slice(count);
    },
    size: () => entries.length,
    bytes: () => totalBytes,
    clear() {
      entries = [];
      totalBytes = 0;
    },
    droppedCount: () => dropped,
  };
}
